import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Section, Cell, Spinner, Input } from '@telegram-apps/telegram-ui';
import { BackButton } from '../components/BackButton';
import { MainButton } from '../components/MainButton';
import { useTelegram } from '../telegram';
import { useCart, CartItem } from '../context/CartContext';

const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
};

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://rentstation.ru';

function CartPage() {
    const { initData } = useTelegram();
    const { invoice, items, loading: cartLoading, removeItem, clearCart } = useCart();

    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    // Form state
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');

    // Form validation errors
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});

    // Refs for form fields (for scroll-to-error)
    const firstNameRef = useRef<HTMLDivElement>(null);
    const lastNameRef = useRef<HTMLDivElement>(null);
    const emailRef = useRef<HTMLDivElement>(null);
    const phoneRef = useRef<HTMLDivElement>(null);

    // Pre-fill form from invoice data
    useEffect(() => {
        if (invoice) {
            if (invoice.person_firstname) setFirstName(invoice.person_firstname);
            if (invoice.person_lastname) setLastName(invoice.person_lastname);
            if (invoice.person_email && !invoice.person_email.endsWith('@telegram.user')) {
                setEmail(invoice.person_email);
            }
            if (invoice.person_phone) setPhone(invoice.person_phone);
        }
    }, [invoice]);

    // Handle remove item
    const handleRemove = useCallback(async (bookingId: number) => {
        try {
            await removeItem(bookingId);
        } catch (err: any) {
            setError(err.message || 'Failed to remove item');
        }
    }, [removeItem]);

    // Handle payment
    const handlePay = useCallback(async () => {
        if (!invoice || submitting) return;

        const authHeader = initData ? `tma ${initData}` : '';

        // Inline validation
        const errors: Record<string, string> = {};
        if (!firstName.trim()) errors.firstName = 'Required';
        if (!lastName.trim()) errors.lastName = 'Required';
        if (!email.trim()) {
            errors.email = 'Required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            errors.email = 'Invalid email format';
        }
        if (!phone.trim()) errors.phone = 'Required';

        setFormErrors(errors);
        if (Object.keys(errors).length > 0) {
            // Scroll to first error field
            const fieldOrder = ['firstName', 'lastName', 'email', 'phone'] as const;
            const refs: Record<string, React.RefObject<HTMLDivElement>> = {
                firstName: firstNameRef,
                lastName: lastNameRef,
                email: emailRef,
                phone: phoneRef,
            };
            for (const field of fieldOrder) {
                const ref = refs[field];
                if (errors[field] && ref.current) {
                    ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    // Try to focus the input inside the wrapper
                    const input = ref.current.querySelector('input');
                    if (input) input.focus();
                    break;
                }
            }
            return;
        }

        setSubmitting(true);

        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
        };
        if (authHeader) {
            headers['Authorization'] = authHeader;
        }

        try {
            // Update invoice with customer info
            const updateRes = await fetch(`${API_BASE_URL}/api/invoice/${invoice.uid}`, {
                method: 'PUT',
                headers,
                body: JSON.stringify({
                    first_name: firstName,
                    last_name: lastName,
                    email: email,
                    phone: phone,
                }),
            });

            if (!updateRes.ok) {
                const data = await updateRes.json();
                throw new Error(data.error || 'Failed to update invoice');
            }

            // Initialize payment
            const payRes = await fetch(`${API_BASE_URL}/api/invoice/${invoice.uid}/pay`, {
                method: 'POST',
                headers,
            });

            if (!payRes.ok) {
                const data = await payRes.json();
                throw new Error(data.error || 'Failed to initialize payment');
            }

            const payData = await payRes.json();

            // Redirect to payment URL
            if (payData.payment_url) {
                window.location.href = payData.payment_url;
            }
        } catch (err: any) {
            setError(err.message);
            setSubmitting(false);
        }
    }, [invoice, initData, firstName, lastName, email, phone, submitting, clearCart]);

    if (cartLoading) {
        return (
            <Section>
                <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                    <Spinner size="m" />
                </div>
            </Section>
        );
    }

    if (error) {
        return (
            <>
                <BackButton />
                <Section header="Ошибка">
                    <Cell>{error}</Cell>
                </Section>
            </>
        );
    }

    if (!invoice || items.length === 0) {
        return (
            <>
                <BackButton />
                <Section header="Корзина пуста">
                    <Cell>Нет товаров в корзине</Cell>
                </Section>
            </>
        );
    }

    const buttonText = submitting
        ? 'Загрузка...'
        : `Оплатить ${invoice.total} \u20BD`;

    return (
        <>
            <BackButton />
            <MainButton text={buttonText} onClick={handlePay} loading={submitting} />

            <Section header="Бронирование">
                {items.map((booking: CartItem) => (
                    <div key={booking.id} style={{ position: 'relative' }}>
                        {booking.item_image && (
                            <div style={{ display: 'flex', justifyContent: 'center', padding: '16px' }}>
                                <img
                                    src={booking.item_image}
                                    alt={booking.item_title}
                                    style={{ maxWidth: '100%', maxHeight: '100px', objectFit: 'contain', borderRadius: 12 }}
                                />
                            </div>
                        )}
                        <Cell subtitle="Товар">{booking.item_title}</Cell>
                        <Cell subtitle="Даты">
                            {formatDate(booking.start)} - {formatDate(booking.end)} ({booking.days} дн.)
                        </Cell>
                        <Cell subtitle="Цена за день">{booking.price_per_day} &#8381;</Cell>
                        <Cell subtitle="Стоимость аренды">{booking.price} &#8381;</Cell>
                        {booking.deposit > 0 && (
                            <Cell subtitle="Залог">{booking.deposit} &#8381;</Cell>
                        )}
                        {items.length > 1 && (
                            <div style={{ padding: '0 16px 16px' }}>
                                <button
                                    onClick={() => handleRemove(booking.id)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: '#ff3b30',
                                        cursor: 'pointer',
                                        padding: '8px 0',
                                        fontSize: '14px',
                                    }}
                                >
                                    Удалить
                                </button>
                            </div>
                        )}
                        {items.length > 1 && items.indexOf(booking) < items.length - 1 && (
                            <div style={{ borderBottom: '1px solid var(--tgui--divider, #e0e0e0)', margin: '0 16px' }} />
                        )}
                    </div>
                ))}
            </Section>

            <Section header="Контактная информация">
                <div ref={firstNameRef}>
                    <Input
                        header="Имя"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        status={formErrors.firstName ? 'error' : undefined}
                        placeholder="Иван"
                    />
                </div>
                <div ref={lastNameRef}>
                    <Input
                        header="Фамилия"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        status={formErrors.lastName ? 'error' : undefined}
                        placeholder="Иванов"
                    />
                </div>
                <div ref={emailRef}>
                    <Input
                        header="Email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        status={formErrors.email ? 'error' : undefined}
                        placeholder="ivan@example.com"
                    />
                </div>
                <div ref={phoneRef}>
                    <Input
                        header="Телефон"
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        status={formErrors.phone ? 'error' : undefined}
                        placeholder="+7 999 123 45 67"
                    />
                </div>
            </Section>

            <Section header="Итого">
                <Cell subtitle="К оплате">{invoice.total} &#8381;</Cell>
            </Section>
        </>
    );
}

export default CartPage;
